import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PartyPopper } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INSTRUMENTS = ["Guitar", "Keyboard", "Violin", "Flute", "Singing", "Composition", "Not sure yet"];
const AGE_GROUPS = ["Child under 12", "Teen", "Adult"];

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition";

export default function LeadCaptureForm({ source = "website", onSuccess, idPrefix = "lead-capture" }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", instrument: "Guitar", age_group: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverMsg, setServerMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const v = k === "phone" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your full name";
    if (!/^[6-9]\d{9}$/.test(form.phone)) errs.phone = "Enter a valid 10-digit Indian mobile number";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid email address";
    if (!form.age_group) errs.age_group = "Please select the student's age group";
    if (!consent) errs.consent = "Please tick the consent box to continue";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    setServerMsg("");
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone,
        email: form.email.trim(),
        course: form.instrument,
        age_group: form.age_group,
        source,
      });
      setDone(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      setServerMsg(
        status === 409
          ? detail || "This phone number is already registered with us. We'll contact you within 24 hours!"
          : detail || "Something went wrong. Please try again, or book on WhatsApp."
      );
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-teal/30 bg-teal/5 p-8 text-center" data-testid={`${idPrefix}-success`}>
        <PartyPopper size={28} className="text-teal mx-auto" />
        <p className="font-body font-extrabold text-lg sm:text-xl text-ink mt-3 leading-snug">
          Thanks! We'll contact you within 24 hours to schedule your free trial.
        </p>
        <p className="text-sm text-muted mt-2">Keep your phone close, {form.name.trim().split(" ")[0]} - we can't wait to hear you play.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-3" data-testid={`${idPrefix}-form`} noValidate>
      <div>
        <input className={inputCls} placeholder="Full Name *" value={form.name} onChange={set("name")} data-testid={`${idPrefix}-name`} aria-label="Full name" />
        {errors.name && <p className="text-[11px] text-brand font-semibold mt-1" data-testid={`${idPrefix}-error-name`}>{errors.name}</p>}
      </div>
      <div>
        <div className="flex items-stretch rounded-xl border border-ink/15 bg-white overflow-hidden focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/15 transition">
          <span className="flex items-center px-3.5 text-sm font-semibold text-ink/60 border-r border-ink/10 bg-sand/50">+91</span>
          <input
            className="flex-1 px-4 py-3 text-sm outline-none bg-transparent"
            placeholder="Phone Number (10 digits) *"
            value={form.phone}
            onChange={set("phone")}
            inputMode="numeric"
            data-testid={`${idPrefix}-phone`}
            aria-label="Phone number"
          />
        </div>
        {errors.phone && <p className="text-[11px] text-brand font-semibold mt-1" data-testid={`${idPrefix}-error-phone`}>{errors.phone}</p>}
      </div>
      <div>
        <input className={inputCls} type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} data-testid={`${idPrefix}-email`} aria-label="Email" />
        {errors.email && <p className="text-[11px] text-brand font-semibold mt-1" data-testid={`${idPrefix}-error-email`}>{errors.email}</p>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <select className={inputCls} value={form.instrument} onChange={set("instrument")} data-testid={`${idPrefix}-instrument`} aria-label="Instrument of interest">
          {INSTRUMENTS.map((i) => (
            <option key={i} value={i}>{i}</option>
          ))}
        </select>
        <div>
          <select className={inputCls} value={form.age_group} onChange={set("age_group")} data-testid={`${idPrefix}-age`} aria-label="Student age group">
            <option value="">Student Age Group *</option>
            {AGE_GROUPS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          {errors.age_group && <p className="text-[11px] text-brand font-semibold mt-1" data-testid={`${idPrefix}-error-age`}>{errors.age_group}</p>}
        </div>
      </div>
      <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid={`${idPrefix}-consent-label`}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 w-5 h-5 shrink-0 accent-[#E0492F]"
          data-testid={`${idPrefix}-consent`}
        />
        <span className="text-xs text-muted leading-relaxed">
          I agree to be contacted by The Music Highway via call, WhatsApp or email, and I accept the{" "}
          <Link to="/privacy" className="text-brand font-semibold hover:underline" data-testid={`${idPrefix}-privacy-link`}>
            Privacy Policy
          </Link>
          .
        </span>
      </label>
      {errors.consent && <p className="text-[11px] text-brand font-semibold -mt-1" data-testid={`${idPrefix}-error-consent`}>{errors.consent}</p>}
      {serverMsg && <p className="text-xs font-semibold text-brand" data-testid={`${idPrefix}-server-msg`}>{serverMsg}</p>}
      <button
        type="submit"
        disabled={loading || !consent}
        data-testid={`${idPrefix}-submit`}
        className="rounded-full bg-ink text-paper font-semibold text-sm py-3.5 hover:bg-brand transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? "Sending..." : "Claim My 2 Free Trial Classes"}
      </button>
    </form>
  );
}
